import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, User, Loader2, Shield, Copy, CheckCircle2, KeyRound, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface InviteTeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  onInviteSent?: () => void;
}

type InviteRole = 'user' | 'company_admin';

interface InviteResult {
  email: string;
  displayName: string;
  roleLabel: string;
  tempPassword: string | null;
  emailSent: boolean;
  emailWarning: string | null;
}

export function InviteTeamMemberModal({
  open,
  onClose,
  companyId,
  companyName,
  onInviteSent,
}: InviteTeamMemberModalProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedRole, setSelectedRole] = useState<InviteRole>('user');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<InviteResult | null>(null);

  const handleInvite = async () => {
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!displayName.trim()) {
      toast.error('Please enter a display name');
      return;
    }

    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/login`;

      const { data, error } = await supabase.functions.invoke('tenant-invite-user', {
        body: {
          email: email.trim().toLowerCase(),
          display_name: displayName.trim(),
          company_id: companyId,
          role: selectedRole,
          redirect_to: redirectTo,
        },
      });

      if (error || !data?.success) {
        throw new Error(data?.error || error?.message || 'Failed to invite user');
      }

      const roleLabel = selectedRole === 'company_admin' ? 'Branch Admin' : 'Team Member';

      // Refresh the team list in the background so the admin sees the new user
      onInviteSent?.();

      // Show the post-invite credentials view so the admin can copy the temp
      // password if needed (especially useful if the email was delayed or failed).
      setResult({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
        roleLabel,
        tempPassword: data?.temp_password || null,
        emailSent: data?.email_sent !== false,
        emailWarning: data?.email_warning || null,
      });
    } catch (err: any) {
      console.error('Error inviting team member:', err);
      toast.error(`Failed to invite: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setDisplayName('');
    setSelectedRole('user');
    setResult(null);
    onClose();
  };

  const handleInviteAnother = () => {
    setEmail('');
    setDisplayName('');
    setSelectedRole('user');
    setResult(null);
  };

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                {result.displayName} added
              </DialogTitle>
              <DialogDescription>
                {result.roleLabel} · {companyName}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {result.tempPassword ? (
                <>
                  <div className="rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <KeyRound className="h-4 w-4 text-indigo-700" />
                      <span className="text-sm font-semibold text-indigo-950">
                        Temporary credentials
                      </span>
                    </div>

                    <div className="space-y-3">
                      <CredentialRow label="Email" value={result.email} onCopy={copy} />
                      <CredentialRow label="Temporary password" value={result.tempPassword} onCopy={copy} />
                    </div>

                    <p className="text-xs text-indigo-900/70 mt-3 leading-relaxed">
                      Share these with {result.displayName} if the email doesn't arrive.
                      They'll be asked to change the password after first login.
                    </p>
                  </div>

                  {result.emailSent ? (
                    <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        Invitation email sent to <strong>{result.email}</strong>.
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        Email could not be sent ({result.emailWarning || 'unknown reason'}).
                        Copy the credentials above and share them directly with {result.displayName}.
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm text-slate-700">
                    <strong>{result.email}</strong> already has a MEDD account.
                    They can sign in with their existing password — no temporary password needed.
                  </p>
                  {result.emailSent && (
                    <p className="text-xs text-emerald-700 mt-2">
                      A password-reset link has been emailed just in case they've forgotten it.
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleInviteAnother}>
                Invite another
              </Button>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription className="sr-only">
                Send an invitation to add a new team member
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Add a new team member to <strong>{companyName}</strong>. They will receive a temporary password to log in.
              </p>

              <div className="space-y-2">
                <Label htmlFor="displayName">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="John Smith"
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@company.com"
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={selectedRole} onValueChange={(val) => setSelectedRole(val as InviteRole)}>
                  <SelectTrigger id="role" className="w-full">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="Select role" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Team Member</span>
                        <span className="text-xs text-muted-foreground">
                          Can access simulations and view own progress
                        </span>
                      </div>
                    </SelectItem>
                    <SelectItem value="company_admin">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Branch Admin</span>
                        <span className="text-xs text-muted-foreground">
                          Manages users, agents, and simulations within one branch (sub-company)
                        </span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                <strong>Note:</strong> You'll see a temporary password on the next screen.
                Share it with the user if the email doesn't arrive.
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleInvite} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Inviting...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CredentialRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (value: string, label: string) => void;
}) {
  return (
    <div>
      <div className="text-xs text-slate-600 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-white border border-indigo-200 rounded-md px-3 py-2 text-sm font-mono text-slate-900 truncate">
          {value}
        </code>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onCopy(value, label)}
          className="shrink-0 h-9 w-9 p-0"
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
